import {useContext, useEffect, useMemo, useRef, useState} from 'react';
import {NetworkContext} from '../../lib/context';
import {useParams} from 'react-router-dom';
import {networkOptimization} from '../../../../lib/api';
import download from '../../../../lib/download';
import {type IExportProps} from './components/Export';
import {IFetchReportsPayload} from '../../lib/types';

export enum ReportsPageTab {
    Roster,
    NetworkAdequacy
}

const tabs = [
    {
        label: 'Roster'
    },
    {
        label: 'Network Adequacy'
    }
];

export type ViewType = {
    name: string;
    text: string;
    index: number;
};

const RosterTabViewTypes: ViewType[] = [
    {name: 'Provider', text: 'Provider Roster', index: 0},
    {name: 'Facility', text: 'Facility Roster', index: 1},
    {name: '', text: 'Combined Roster', index: 2}
];

export enum NetworkAdequacyTabViewEnum {
    Providers,
    AvailableProviders,
    AdequacyScores
}

const NetworkAdequacyTabViewTypes: ViewType[] = [
    {name: '', text: 'Providers', index: 0},
    {name: '', text: 'Available Providers', index: 1},
    {name: '', text: 'Adequacy Scores', index: 2}
];

const DOWNLOAD_PROVIDERS_REPORT_ITEM = 'providersReport';
const DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM = 'availableProvidersReport';
const DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM = 'adequacyScoresReport';
const DOWNLOAD_STANDARD_REPORTING_ITEM = 'standardReporting';

const useReportsPageController = () => {

    const [exportingProviderRoster, setExportingProviderRoster] = useState<string | null>(null);
    const [exportingFacilityRoster, setExportingFacilityRoster] = useState<string | null>(null);
    const [exportingCombinedRoster, setExportingCombinedRoster] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<ReportsPageTab>(ReportsPageTab.Roster);
    const [selectedViewType, setSelectedViewType] = useState<ViewType>(RosterTabViewTypes[0]);

    const networkOptimizationStore = useContext(NetworkContext);
    const {rosterId, revisionId} = useParams();

    useEffect(() => {
        if (exportingProviderRoster) {
            networkOptimization
                .downloadProviderRoster(
                    networkOptimizationStore.runId!,
                    networkOptimizationStore.revisionId!,
                    exportingProviderRoster,
                    new AbortController().signal
                )
                .then((response) => {
                    download(response, `provider-roster-${exportingProviderRoster}.zip`, 'application/zip');
                    setExportingProviderRoster(null);
                });
        }
    }, [exportingProviderRoster]);

    useEffect(() => {
        if (exportingFacilityRoster) {
            networkOptimization
                .downloadFacilityRoster(
                    networkOptimizationStore.runId!,
                    networkOptimizationStore.revisionId!,
                    exportingFacilityRoster,
                    new AbortController().signal
                )
                .then((response) => {
                    download(response, `facility-roster-${exportingFacilityRoster}.zip`, 'application/zip');
                    setExportingFacilityRoster(null);
                });
        }
    }, [exportingFacilityRoster]);

    useEffect(() => {
        if (exportingCombinedRoster) {
            networkOptimization
                .downloadCombinedRoster(
                    networkOptimizationStore.runId!,
                    networkOptimizationStore.revisionId!,
                    exportingCombinedRoster,
                    new AbortController().signal
                )
                .then((response) => {
                    download(response, `combined-roster.zip`, 'text/plain');
                    setExportingCombinedRoster(null);
                });
        }
    }, [exportingCombinedRoster]);

    const rosterTabExportMenuItems: IExportProps['menuItems'] = [
        {
            disabled: Boolean(exportingProviderRoster),
            label: 'Provider Roster', 
            onClick: () =>
                setExportingProviderRoster(
                    networkOptimizationStore.appliedGeographyFilter.standard_type.toLowerCase()
                )
        },
        {
            disabled: Boolean(exportingFacilityRoster),
            label: 'Facility Roster', 
            onClick: () =>
                setExportingFacilityRoster(
                    networkOptimizationStore.appliedGeographyFilter.standard_type.toLowerCase()
                )
        },
        {
            disabled: Boolean(exportingCombinedRoster),
            label: 'Combined Roster', 
            onClick: () =>
                setExportingCombinedRoster(
                    networkOptimizationStore.appliedGeographyFilter.standard_type.toLowerCase()
                )
        }
    ];

    const reportPayload : IFetchReportsPayload = useMemo(() => ({
        roster_id: networkOptimizationStore.runId!,
        revision_id: networkOptimizationStore.revisionId,
        standard_type: networkOptimizationStore.geographyFilters.standard_type,
        standard_version: networkOptimizationStore.geographyFilters.standard_version,
        patient_roster_id: +networkOptimizationStore.geographyFilters.patient_roster_id,
        specialty_code: networkOptimizationStore.specialtyFilterCode ? networkOptimizationStore.specialtyFilterCode.split('|') : [],
        state: networkOptimizationStore.geographyFilters.state ? networkOptimizationStore.geographyFilters.state.split('|') : [],
        county: networkOptimizationStore.geographyFilters.county ? networkOptimizationStore.geographyFilters.county.split('|') : [],
        signal: new AbortController().signal
    }), [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.geographyFilters.standard_type,
        networkOptimizationStore.geographyFilters.standard_version,
        networkOptimizationStore.geographyFilters.patient_roster_id,
        networkOptimizationStore.specialtyFilterCode
    ]);

    const disableExportReports = useMemo(() => {
        const no = networkOptimizationStore;
        const disabled = !([
            no.runId,
            no.revisionId >= 0 ? true : false,
            no.appliedGeographyFilter.standard_type,
            no.appliedGeographyFilter.standard_version,
            no.appliedGeographyFilter.patient_roster_name
        ].every(Boolean));
        return {
            providersReport: disabled || !!no.downloadingItems[DOWNLOAD_PROVIDERS_REPORT_ITEM],
            availableProvidersReport: disabled || !!no.downloadingItems[DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM],
            adequacyScoresReport: disabled || !!no.downloadingItems[DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM],
            standardReporting: disabled || !!no.downloadingItems[DOWNLOAD_STANDARD_REPORTING_ITEM]
        };
    }, [
        networkOptimizationStore.runId,
        networkOptimizationStore.revisionId,
        networkOptimizationStore.appliedGeographyFilter.standard_type,
        networkOptimizationStore.appliedGeographyFilter.standard_version,
        networkOptimizationStore.appliedGeographyFilter.patient_roster_name,
        networkOptimizationStore.appliedGeographyFilter.state,
        networkOptimizationStore.appliedGeographyFilter.county,
        networkOptimizationStore.specialtyFilter,
        selectedViewType,
        networkOptimizationStore.downloadingItems
    ]);

    const networkAdequacyTabExportMenuItems: IExportProps['menuItems'] = [
        {
            disabled: disableExportReports.standardReporting,
            label: 'Standard Reporting', 
            onClick: () => networkOptimizationStore.downloadReport(
                reportPayload,
                'standard-reporting',
                DOWNLOAD_STANDARD_REPORTING_ITEM
            )
        },
        {
            disabled: disableExportReports.providersReport,
            label: 'Providers', 
            onClick: () => networkOptimizationStore.downloadReport(
                reportPayload,
                'providers',
                DOWNLOAD_PROVIDERS_REPORT_ITEM
            )
        },
        {
            disabled: disableExportReports.availableProvidersReport,
            label: 'Available Providers', 
            onClick: () => networkOptimizationStore.downloadReport(
                reportPayload,
                'available-providers',
                DOWNLOAD_AVAILABLE_PROVIDERS_REPORT_ITEM
            )
        },
        {
            disabled: disableExportReports.adequacyScoresReport,
            label: 'Adequacy Scores', 
            onClick: () => networkOptimizationStore.downloadReport(
                reportPayload,
                'adequacy-scores',
                DOWNLOAD_ADEQUACY_SCORES_REPORT_ITEM
            )
        }
    ];

    const exportMenuItems = activeTab === ReportsPageTab.Roster ? rosterTabExportMenuItems : networkAdequacyTabExportMenuItems;

    const currentViewTypes = useMemo(() => {
        return activeTab === ReportsPageTab.Roster ? RosterTabViewTypes : NetworkAdequacyTabViewTypes;
    }, [activeTab]);

    useEffect(() => {
        setSelectedViewType(currentViewTypes[0]);
    }, [currentViewTypes]);

    const isInitialTab = useRef(true);
    useEffect(() => {
        if (isInitialTab.current) {
            isInitialTab.current = false;
            return;
        }
        networkOptimizationStore.resetGeographyFilter(['standard_type']);
    }, [activeTab]);

    const onActiveTabChange = (activeTabIndex: number) => {
        setActiveTab(activeTabIndex);
    };

    const onSelectedViewTypeChange = (viewType: ViewType) => {
        setSelectedViewType(viewType);
    };

    const generateReportPath = useMemo(() => {
        if (!rosterId || !revisionId) return '#';
        const _ = networkOptimizationStore;
        const searchParams = new URLSearchParams({
            standard_type: _.geographyFilters.standard_type,
            standard_version: _.geographyFilters.standard_version,
            patient_roster_id: _.geographyFilters.patient_roster_id,
            state: _.geographyFilters.state,
            // TODO: County and specialty filters are disabled for generate report for now, may need to re-enable in the future
            // county: _.geographyFilters.county,
            // specialty_filter_code: _.specialtyFilterCode
        });
        return `/network-optimization/${rosterId}/${revisionId}/generate-report?${searchParams.toString()}`;
    }, [
        rosterId,
        revisionId,
        networkOptimizationStore.geographyFilters.standard_type,
        networkOptimizationStore.geographyFilters.standard_version,
        networkOptimizationStore.geographyFilters.patient_roster_id,
        networkOptimizationStore.geographyFilters.state,
        // TODO: Re-enable when county and specialty filters are needed for generate report
        // networkOptimizationStore.geographyFilters.county,
        // networkOptimizationStore.specialtyFilterCode,
    ]);

    return {
        data: {
            tabs,
            rosterId,
            isExporting: Boolean(
                exportingProviderRoster || 
                exportingFacilityRoster || 
                exportingCombinedRoster ||
                Object.values(networkOptimizationStore.downloadingItems).some(Boolean)
            ),
            selectedViewType,
            networkOptimizationStore,
            activeTab,
            exportMenuItems,
            currentViewTypes,
            disableExportReports,
            generateReportPath
        },
        methods: {
            onSelectedViewTypeChange,
            onActiveTabChange,
        }
    };
};

export type IReportsPageController = ReturnType<typeof useReportsPageController>;

export default useReportsPageController;
